import { createContext, use } from 'react';
import type { TimerResult } from '../../hooks/useTimerState';

// TODO v2: Web notifications for long-running timers (>X hours)
// TODO v2: Keyboard shortcuts (Ctrl+Shift+T to start/stop)

export interface TimerContextValue {
  /** Whether a timer is actively running (not paused) */
  isRunning: boolean;
  /** Whether a timer exists but is paused */
  isPaused: boolean;
  /** Whether a timer exists at all (running or paused) */
  hasTimer: boolean;
  /** Whether the timer was auto-paused after exceeding the 24h threshold */
  autoPaused: boolean;
  /** Elapsed seconds since timer started (including paused segments) */
  elapsed: number;
  /** Current timer metadata (matter, client, names) */
  matterId: string | null;
  clientId: string | null;
  matterName: string | null;
  clientName: string | null;
  /** Start a new timer for a matter */
  startTimer: (matterId: string, clientId: string, matterName: string, clientName: string) => void;
  /** Pause the running timer */
  pauseTimer: () => void;
  /** Resume a paused timer */
  resumeTimer: () => void;
  /** Pause the timer and open the activity wizard. Timer stays alive so user can resume if they cancel. */
  saveTimer: () => void;
  /** Permanently clear the timer (called after activity is successfully created) */
  stopTimer: () => void;
  /** Discard the timer without creating an activity */
  discardTimer: () => void;
  /** Result from saveTimer, consumed by the activities page to open the wizard */
  pendingTimerResult: TimerResult | null;
  /** Clear the pending result after the activities page has consumed it */
  clearPendingResult: () => void;
}

export const TimerContext = createContext<TimerContextValue | null>(null);

export function useTimer(): TimerContextValue {
  const context = use(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
}
